import * as React from 'react';
import Quagga from 'quagga';
import jsQR from 'jsqr';
import torchSVG from './torch.svg';
import './styles.scss';

interface Props {
  consecutiveMatchCount: number;
  decoder: string | string[];
  errorThreshold: number;
  fullscreen: boolean;
  onCancel: () => any;
  onScan: (code: string, scanType: 'qr' | 'bar', meta: any) => any;
  onSubmit: (code: string) => any;
  scanQRCodes: boolean;
  scanBarcodes: boolean;
  scansPerSecond: number;
  scanWindowSize?: { width: number; height: number };
  showDecoderSelect: boolean;
  useScanWindow: boolean;
}

interface State {
  hasZoom: boolean;
  hasTorch: boolean;
  torch: boolean;
  error: string;
  scan: any;
  decoder: string[];
}

class BarcodeScanner extends React.Component<Props, State> {
  static defaultProps = {
    consecutiveMatchCount: 2,
    decoder: ['code_128'],
    errorThreshold: 0.75,
    fullscreen: false,
    scanQRCodes: true,
    scanBarcodes: true,
    onCancel: () => null,
    onSubmit: () => null,
    onScan: () => null,
    scansPerSecond: 10,
    scanWindowSize: {
      width: 0.4,
      height: 0.4,
    },
    showDecoderSelect: false,
    useScanWindow: false,
  };

  readonly state: State = {
    error: '',
    hasZoom: false,
    hasTorch: false,
    torch: false,
    scan: {},
    decoder: [],
  };

  lastCode: string | null = null;
  lastCodes: string[] = [];
  frame: number | null = null;
  mounted = false;
  cameraRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.state.decoder = Array.isArray(props.decoder) ? props.decoder : [props.decoder];
    this.rescan = this.rescan.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.initScanners();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.stopScanners();

    // make sure the torch is off!
    this.applySettings('torch', false);
  }

  getDecoderValue() {
    const ret: object[] = [];

    for (const decoder of this.state.decoder) {
      if (decoder === 'ean_extended') {
        ret.push({
          format: 'ean_reader',
          config: {
            supplements: ['ean_5_reader', 'ean_2_reader'],
          },
        });
      } else {
        ret.push({
          format: `${decoder}_reader`,
          config: {},
        });
      }
    }

    return ret;
  }

  setDecoder(value: string) {
    Quagga.stop();
    this.setState({ decoder: [value] }, () => this.initQuagga());
  }

  getScanWindow() {
    const drawingCanvas = Quagga.canvas.dom.overlay;

    if (!this.props.useScanWindow) {
      return {
        x: 0,
        y: 0,
        width: drawingCanvas.width,
        height: drawingCanvas.height,
      };
    }

    // dimensions of window
    const width = this.props.scanWindowSize!.width || 0.4;
    const height = this.props.scanWindowSize!.height || 0.4;

    return {
      x: (drawingCanvas.getAttribute('width') * (1 - width)) / 2,
      y: (drawingCanvas.getAttribute('height') * (1 - height)) / 2,
      width: drawingCanvas.getAttribute('width') * width,
      height: drawingCanvas.getAttribute('height') * height,
      fillStyle: 'rgba(225,225,225,0.3)',
    };
  }

  getScanLine() {
    const drawingCanvas = Quagga.canvas.dom.overlay;

    if (!this.props.useScanWindow) {
      return {
        x: 0,
        y: 0,
        width: drawingCanvas.width,
        height: drawingCanvas.height,
      };
    }

    // dimensions of window
    const width = 300;
    const height = 2;

    return {
      x: (drawingCanvas.getAttribute('width') - width) / 2,
      y: (drawingCanvas.getAttribute('height') - height) / 2,
      width,
      height,
      fillStyle: 'rgba(225,0,0,0.8)',
    };
  }

  stopScanners() {
    Quagga.stop();
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  drawScanWindow() {
    if (!this.props.useScanWindow) {
      return;
    }

    const drawingCtx = Quagga.canvas.ctx.overlay;

    const { x, y, width, height, fillStyle } = this.getScanWindow();

    drawingCtx.fillStyle = fillStyle;
    drawingCtx.fillRect(x, y, width, height);
    drawingCtx.stroke();
  }

  drawScanLine() {
    if (!this.props.useScanWindow) {
      return;
    }

    const drawingCtx = Quagga.canvas.ctx.overlay;

    const { x, y, width, height, fillStyle } = this.getScanLine();

    drawingCtx.fillStyle = fillStyle;
    drawingCtx.fillRect(x, y, width, height);
    drawingCtx.stroke();
  }

  initScanners() {
    this.initQuagga();
    if (this.props.scanQRCodes) {
      this.initQrScanner();
    }
  }

  initQrScanner() {
    // draw a line given a canvas
    const drawLine = (canvasCtx, begin, end, color) => {
      canvasCtx.beginPath();
      canvasCtx.moveTo(begin.x, begin.y);
      canvasCtx.lineTo(end.x, end.y);
      canvasCtx.lineWidth = 4;
      canvasCtx.strokeStyle = color;
      canvasCtx.stroke();
    };

    const tick = () => {
      if (!this.mounted) {
        return;
      }

      const canvas = Quagga.canvas.dom.image;

      if (canvas) {
        const canvasCtx = canvas.getContext('2d');
        const { x, y, width, height } = this.getScanWindow();

        const imageData = canvasCtx.getImageData(x, y, width, height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code) {
          this.props.onScan(code.data, 'qr', code);

          const { location } = code;
          const red = '#F00';

          // draw red box around scanned QRCode
          // offset location based on scan window
          location.topLeftCorner.x += x;
          location.topLeftCorner.y += y;
          location.topRightCorner.x += x;
          location.topRightCorner.y += y;
          location.bottomRightCorner.x += x;
          location.bottomRightCorner.y += y;
          location.bottomLeftCorner.x += x;
          location.bottomLeftCorner.y += y;
          drawLine(canvasCtx, location.topLeftCorner, location.topRightCorner, red);
          drawLine(canvasCtx, location.topRightCorner, location.bottomRightCorner, red);
          drawLine(canvasCtx, location.bottomRightCorner, location.bottomLeftCorner, red);
          drawLine(canvasCtx, location.bottomLeftCorner, location.topLeftCorner, red);
          this.setState({
            scan: { data: canvas.toDataURL(), code: code.data },
          });
          this.stopScanners();
          return;
        }
      }

      // this delay significantly lowers CPU load
      setTimeout(() => {
        this.frame = requestAnimationFrame(tick);
      }, 1000 / this.props.scansPerSecond);
    };

    this.frame = requestAnimationFrame(tick);
  }

  initQuagga() {
    const scanAreaWidth = (100 - this.props.scanWindowSize!.width * 100).toString() + '%';
    const scanAreaHeight = (100 - this.props.scanWindowSize!.height * 100).toString() + '%';

    // Quagga API: https://github.com/serratus/quaggaJS#api
    Quagga.init(
      {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: this.cameraRef.current,
          constraints: {
            facingMode: 'environment',
          },
        },
        locator: {
          patchSize: 'medium',
          halfSample: true,
        },
        frequency: this.props.scanBarcodes ? this.props.scansPerSecond : 0, // scans per second
        decoder: {
          readers: this.getDecoderValue(),
        },
        area: {
          // defines rectangle of the detection/localization area
          top: scanAreaWidth, // top offset
          right: scanAreaHeight, // right offset
        },
        locate: true,
      },
      (err: Error) => {
        if (err) {
          // usually fails if user didn't give camera access
          this.setState({ error: err.message });
          return;
        }
        this.checkCapabilities();
        Quagga.start();

        this.drawScanWindow();
        // this.drawScanLine();
      }
    );

    Quagga.onProcessed((result) => {
      // Quagga processed possible barcode
      if (!this.props.scanBarcodes) {
        return;
      }

      const drawingCtx = Quagga.canvas.ctx.overlay;
      const drawingCanvas = Quagga.canvas.dom.overlay;

      const isBoxInScanWindow = (box) => {
        // box corners
        // 0 bottom left
        // 1 top left
        // 2 top right
        // 3 bottom right

        const { x, y, width, height } = this.getScanWindow();

        // get center point of box
        const centerX = (box[0][0] + box[2][0]) / 2;
        const centerY = (box[0][1] + box[2][1]) / 2;

        // determine if center point lands in scan window
        return !!(centerX > x && centerX < x + width && centerY > y && centerY < y + height);
      };

      if (result) {
        if (result.boxes) {
          drawingCtx.clearRect(
            0,
            0,
            parseInt(drawingCanvas.getAttribute('width')),
            parseInt(drawingCanvas.getAttribute('height'))
          );
          this.drawScanWindow();
          // this.drawScanLine();

          result.boxes
            .filter((box) => {
              return box !== result.box;
            })
            .filter((box) => {
              if (!this.props.useScanWindow) {
                return true;
              }

              return isBoxInScanWindow(box);
            })
            .forEach((box) => {
              Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
                color: 'green',
                lineWidth: 2,
              });
            });
        }

        if (result.box) {
          if (!this.props.useScanWindow || isBoxInScanWindow(result.box)) {
            Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
              color: '#00F',
              lineWidth: 2,
            });

            if (result.codeResult && result.codeResult.code) {
              Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, drawingCtx, { color: 'red', lineWidth: 3 });
            }
          }
        }
      }
    });

    Quagga.onDetected(({ codeResult, line }) => {
      // Quagga detected a barcode
      if (!this.props.scanBarcodes) {
        return;
      }

      // get the middle of line
      const centerX = (line[1].x + line[0].x) / 2;
      const centerY = (line[1].y + line[0].y) / 2;

      if (this.props.useScanWindow) {
        const { x, y, width, height } = this.getScanWindow();

        if (!(centerX > x && centerX < x + width && centerY > y && centerY < y + height)) {
          return;
        }
      }

      const { code } = codeResult;

      // get array of error values and check calculate average, if avg is greater than threshold, ignore
      const decodedErrors = codeResult.decodedCodes.map((o) => o.error).filter((n) => n !== undefined);
      const errorAvg = decodedErrors.reduce((total: number, num: number) => total + num, 0) / decodedErrors.length;

      if (errorAvg > this.props.errorThreshold || !code) {
        // too much error, ignore
        return;
      }

      this.props.onScan(code, 'bar', codeResult);

      // check track of codes that were detected
      this.lastCodes.unshift(code);
      if (this.lastCodes.length > 10) {
        this.lastCodes = this.lastCodes.slice(0, 10);
      }

      // get the amount of times code was detected in a row
      let equalCount = 0;
      for (const lastCode of this.lastCodes) {
        if (code === lastCode) {
          equalCount++;
        } else {
          break;
        }
      }

      if (equalCount < this.props.consecutiveMatchCount) {
        // at least have 2 matching scans in a row
        return;
      }

      this.setState({
        scan: {
          data: Quagga.canvas.dom.image.toDataURL(),
          code,
          overlay: Quagga.canvas.dom.overlay.toDataURL(),
        },
      });
      this.lastCode = code;
      this.lastCodes = [];

      Quagga.stop();
    });
  }

  checkCapabilities() {
    const track = Quagga.CameraAccess.getActiveTrack();

    if (typeof track.getCapabilities !== 'function') {
      return;
    }

    const { zoom, torch } = track.getCapabilities();
    this.setState({ hasZoom: !!zoom, hasTorch: !!torch || true });
  }

  applySettings(setting, value) {
    const track = Quagga.CameraAccess.getActiveTrack();

    if (!track) {
      return;
    }

    switch (setting) {
      case 'zoom':
        track.applyConstraints({ advanced: [{ zoom: parseFloat(value) }] });
        break;

      case 'torch':
        this.setState({ torch: value });
        track.applyConstraints({ advanced: [{ torch: value }] });
        break;

      // no default
    }
  }

  rescan() {
    this.setState({ scan: {} }, () => {
      this.initScanners();
    });
  }

  renderTorch() {
    const { hasTorch, torch } = this.state;

    if (!hasTorch) {
      return;
    }

    return (
      <div
        className={`torch ${torch ? 'on' : 'off'}`}
        onClick={() => {
          this.applySettings('torch', !torch);
        }}
      >
        <img src={torchSVG} alt="torch" title="Flash light toggle" />
      </div>
    );
  }

  renderControls() {
    const { hasZoom, hasTorch } = this.state;

    if (!hasZoom && !hasTorch) {
      return null;
    }

    // zoom is currently ignored
    return (
      <div className="controls">
        {this.renderTorch()}
        {this.renderDecodersSelect()}
        {this.props.onCancel && (
          <div className="cancel-wrapper">
            <button type="button" className="cancel" onClick={this.props.onCancel}>
              Cancel
            </button>
          </div>
        )}
        <div style={{ clear: 'both' }} />
      </div>
    );
  }

  renderDecodersSelect() {
    if (!this.props.showDecoderSelect) {
      return null;
    }

    return (
      <div className="decoder-select-wrapper">
        <select
          placeholder="select"
          onChange={(e) => {
            this.setDecoder(e.target.value);
          }}
          value={this.state.decoder}
        >
          <option value="code_128">Code 128</option>
          <option value="code_39">Code 39</option>
          <option value="code_39_vin">Code 39 VIN</option>
          <option value="ean">EAN</option>
          <option value="ean_extended">EAN-extended</option>
          <option value="ean_8">EAN-8</option>
          <option value="upc">UPC</option>
          <option value="upc_e">UPC-E</option>
          <option value="codabar">Codabar</option>
          <option value="i2of5">I2of5</option>
          <option value="2of5">Standard 2 of 5</option>
          <option value="code_93">Code 93</option>
        </select>
      </div>
    );
  }

  renderScan() {
    const { scan } = this.state;

    if (!scan.code) {
      return null;
    }

    return (
      <>
        <div className="scan-snapshot">
          <div className="overlay-value">
            Value: <b>{scan.code}</b>
          </div>
          <div className="overlay-value-bg" />
          <img className="overlay-image" alt="" src={scan.overlay} />
          <img alt={scan.code} src={scan.data} />
        </div>
        <div className="controls">
          <button type="button" className="pull-right btn-success" onClick={() => this.props.onSubmit(scan.code)}>
            Accept
          </button>
          <button type="button" className="pull-left" onClick={this.rescan}>
            Rescan
          </button>
          <div style={{ clear: 'both' }} />
        </div>
      </>
    );
  }

  renderCameraStream() {
    if (this.state.scan.code) {
      return null;
    }

    return (
      <>
        <div ref={this.cameraRef} className="camera-stream" />
        {this.renderControls()}
      </>
    );
  }

  render() {
    return (
      <div className={`react-barcode-scanner ${this.props.fullscreen ? 'fullscreen' : ''}`}>
        <div className="close" onClick={this.props.onCancel} />
        {!!this.state.error && <div>{this.state.error}</div>}
        {this.renderScan()}
        {this.renderCameraStream()}
      </div>
    );
  }
}

export default BarcodeScanner;
